import React, { useState, useEffect } from 'react';
import { RefreshCw, Save, X } from 'lucide-react';
import { dashboardApi } from '../api/dashboardApi';
import { AIApiSettings } from '../types/License';
import './AISettingsPage.css';

type Provider = 'openrouter' | 'google_genai';

const PROVIDER_LABELS: Record<Provider, string> = {
  openrouter: 'OpenRouter',
  google_genai: 'Google GenAI',
};

const MODEL_HINTS: Record<Provider, string> = {
  openrouter: 'e.g. z-ai/glm-4.5-air:free, anthropic/claude-3.5-sonnet',
  google_genai: 'e.g. gemini-2.5-flash, gemini-2.5-pro',
};

const AISettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState<AIApiSettings>({
    model: 'openrouter',
    openrouter: { key: '', model: 'z-ai/glm-4.5-air:free', reasoning: true },
    google_genai: { key: '', model: 'gemini-2.5-flash', reasoning: false },
  });

  useEffect(() => {
    fetchAISettings();
  }, []);

  const fetchAISettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dashboardApi.getAIApiSettings();
      if (response.ai_settings) {
        setFormData(response.ai_settings);
      }
    } catch (err) {
      setError('Failed to fetch AI settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await dashboardApi.updateAIApiSettings(formData);
      setSuccessMessage('Settings saved and will sync to retail clients on login');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      setError('Failed to save AI settings');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const updateProviderConfig = (
    provider: Provider,
    field: string,
    value: string | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        [field]: value,
      },
    }));
  };

  const activeProvider = formData.model;
  const activeConfig = formData[activeProvider];

  if (loading) {
    return (
      <div className="ai-page ai-loading">
        <RefreshCw size={20} className="ai-spin" />
        <div>Loading AI settings…</div>
      </div>
    );
  }

  return (
    <div className="ai-page">
      <div className="ai-toolbar">
        <div className="ai-tabs">
          {(['openrouter', 'google_genai'] as Provider[]).map((p) => (
            <button
              key={p}
              type="button"
              className={`ai-tab ${activeProvider === p ? 'ai-tab--active' : ''}`}
              onClick={() => setFormData((prev) => ({ ...prev, model: p }))}
            >
              {PROVIDER_LABELS[p]}
            </button>
          ))}
        </div>
        <span className="ai-toolbar-note">Synced to clients with retail platform</span>
        <button type="button" className="ai-btn" onClick={fetchAISettings} disabled={loading}>
          <RefreshCw size={14} className={loading ? 'ai-spin' : ''} />
          Refresh
        </button>
        <button type="button" className="ai-btn ai-btn--primary" onClick={handleSave} disabled={saving}>
          <Save size={14} />
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {successMessage && (
        <div className="ai-toast ai-toast--ok">
          <span>{successMessage}</span>
          <button type="button" onClick={() => setSuccessMessage(null)} aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}
      {error && (
        <div className="ai-toast ai-toast--err">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}

      <div className="ai-panel">
        <div className="ai-field">
          <label htmlFor="ai-key">API key</label>
          <input
            id="ai-key"
            type="password"
            value={activeConfig.key}
            onChange={(e) => updateProviderConfig(activeProvider, 'key', e.target.value)}
            placeholder={activeProvider === 'openrouter' ? 'sk-or-v1-…' : 'AIza…'}
            autoComplete="off"
          />
        </div>

        <div className="ai-field">
          <label htmlFor="ai-model">Model</label>
          <input
            id="ai-model"
            type="text"
            value={activeConfig.model}
            onChange={(e) => updateProviderConfig(activeProvider, 'model', e.target.value)}
            placeholder={activeProvider === 'openrouter' ? 'z-ai/glm-4.5-air:free' : 'gemini-2.5-flash'}
          />
          <div className="ai-hint">{MODEL_HINTS[activeProvider]}</div>
        </div>

        <div className="ai-toggle-row">
          <div>
            <span className="ai-toggle-label">Reasoning mode</span>
            <span className="ai-toggle-hint">For thinking / chain-of-thought models</span>
          </div>
          <label className="ai-switch">
            <input
              type="checkbox"
              checked={activeConfig.reasoning}
              onChange={(e) => updateProviderConfig(activeProvider, 'reasoning', e.target.checked)}
            />
            <span className="ai-switch-slider" />
          </label>
        </div>
      </div>

      <div className="ai-info">
        <strong>Active provider:</strong> {PROVIDER_LABELS[activeProvider]}. Settings apply globally and push to
        clients on login when platform includes <strong>retail</strong> or <strong>attendance+retail</strong>.
        Office-only clients do not receive AI config.
      </div>
    </div>
  );
};

export default AISettingsPage;
